import {
  type EnvironmentProviders,
  InjectionToken,
  type Signal,
  computed,
  inject,
  makeEnvironmentProviders,
  signal,
} from '@angular/core';

/**
 * The handful of strings the design system renders on its own — close buttons, search
 * placeholders, the command palette's chrome, the sparkline trend words. Components still expose
 * per-instance inputs for one-off copy; this is the app-wide default so a consumer never has to
 * remember to translate a built-in label.
 */
export interface NbUiText {
  /** Accessible name of every close button (dialog, drawer). */
  close: string;
  /** Accessible name of a search field. */
  search: string;
  /** Placeholder of a search field / topbar search. */
  searchPlaceholder: string;
  /** Accessible name of the primary sidebar. */
  mainMenu: string;
  /** Prefix read before a sidebar badge count ("Antall: 3"). */
  count: string;
  /** Accessible name of the command palette dialog. */
  commandPalette: string;
  /** Placeholder inside the command palette. */
  commandPlaceholder: string;
  /** Shown when a search or palette has no results. */
  noHits: string;
  /** Shown while results are loading. */
  searching: string;
  /** Live-region text for exactly one hit. */
  hitsOne: string;
  /** Live-region text for n hits — `{count}` is replaced. */
  hitsMany: string;
  /** Visually hidden word before a positive trend delta. */
  trendUp: string;
  /** Visually hidden word before a negative trend delta. */
  trendDown: string;
}

/**
 * Norwegian (bokmål) fallbacks — the product's default language. An app that ships more locales
 * overrides them with {@link provideNbUiText}; these keep `katalog` and any unconfigured consumer
 * rendering real copy instead of keys.
 */
export const NB_UI_TEXT_DEFAULT: NbUiText = {
  close: 'Lukk',
  search: 'Søk',
  searchPlaceholder: 'Søk…',
  mainMenu: 'Hovedmeny',
  count: 'Antall',
  commandPalette: 'Kommandopalett',
  commandPlaceholder: 'Søk i sider, handlinger, saker, beboere og dokumenter…',
  noHits: 'Ingen treff',
  searching: 'Søker …',
  hitsOne: '1 treff',
  hitsMany: '{count} treff',
  trendUp: 'Opp',
  trendDown: 'Ned',
};

/** App-wide design-system copy, as a signal so a runtime locale switch re-renders the components. */
export const NB_UI_TEXT = new InjectionToken<Signal<NbUiText>>('NB_UI_TEXT');

/**
 * Register translated design-system copy. The factory runs in an injection context, so it may
 * `inject(TranslateService)` and return a `computed` that reads the active language:
 *
 * @example
 * provideNbUiText(() => {
 *   const t = inject(TranslateService);
 *   return computed(() => (t.currentLang(), { close: t.instant('common.close'), … }));
 * })
 */
export function provideNbUiText(factory: () => Signal<Partial<NbUiText>>): EnvironmentProviders {
  return makeEnvironmentProviders([
    {
      provide: NB_UI_TEXT,
      useFactory: (): Signal<NbUiText> => {
        const provided = factory();
        return computed(() => ({ ...NB_UI_TEXT_DEFAULT, ...provided() }));
      },
    },
  ]);
}

const FALLBACK = signal(NB_UI_TEXT_DEFAULT).asReadonly();

/** Read the app's design-system copy, falling back to the Norwegian defaults. Call in an injection context. */
export function injectNbUiText(): Signal<NbUiText> {
  return inject(NB_UI_TEXT, { optional: true }) ?? FALLBACK;
}
